import { PButtonPure, PHeading } from '@porsche-design-system/components-react';
import { getMediaQueryMin, spacingStaticMedium } from '@porsche-design-system/components-react/styles';
import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import useHeader from '../useHeader';

const StackedLayoutHeaderContainer = styled.div({
    boxSizing: 'content-box',
    marginBottom: '36px',
});

const BackButtonContainer = styled.div({
    height: '20px',
    marginBottom: '16px',
});

const HeaderContainer = styled.div<{ moreThanOneButton: boolean }>(props => ({
    display: 'flex',
    flexDirection: props.moreThanOneButton ? 'column' : 'row',
    justifyContent: props.moreThanOneButton ? 'none' : 'space-between',
    gap: props.moreThanOneButton ? spacingStaticMedium : 0,
    [getMediaQueryMin('s')]: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
}));

const ButtonsContainer = styled.div<{ moreThanOneButton: boolean }>(props => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
    width: props.moreThanOneButton ? '100%' : 'auto',
    'p-button': {
        flex: 1,
    },
    [getMediaQueryMin('s')]: {
        width: 'auto',
        'p-button': {
            flex: 'none',
        },
    },
}));

type StackedLayoutHeaderProps = {
    title: string;
    hasBack?: boolean;
    backText?: string;
    onBack?: Function;
    buttons?: ReactNode[];
};

const StackedLayoutHeader = ({ title, hasBack, backText, onBack, buttons = [] }: StackedLayoutHeaderProps) => {
    const { t } = useTranslation('common');
    const render = useHeader();
    const navigate = useNavigate();

    return render(
        <StackedLayoutHeaderContainer>
            <BackButtonContainer>
                {hasBack && (
                    <PButtonPure
                        icon="arrow-left"
                        inert={undefined}
                        onClick={() => onBack?.() ?? navigate(-1)}
                        size="xx-small"
                        type="button"
                    >
                        {backText || t('common:back')}
                    </PButtonPure>
                )}
            </BackButtonContainer>

            <HeaderContainer moreThanOneButton={buttons?.length > 1}>
                <PHeading color="primary" inert={undefined} size="x-large" tag="h1">
                    {title}
                </PHeading>
                {buttons?.length > 0 && (
                    <ButtonsContainer moreThanOneButton={buttons?.length > 1}>
                        {buttons?.map(button => button)}
                    </ButtonsContainer>
                )}
            </HeaderContainer>
        </StackedLayoutHeaderContainer>
    );
};

export default StackedLayoutHeader;
